import { useEffect, useState } from "react";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

import { makeGetApiCall } from "../../../shared/makeApiCall";

const statusStyles = {
  queued: "bg-yellow-100 text-yellow-800",
  processing: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
};

function displayDate(value) {
  if (!value) {
    return "—";
  }

  return new Date(value).toLocaleString();
}

export default function RunAllHistory(props) {
  const { logout, setOpen } = props;
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    let timer;

    const loadRuns = async () => {
      const callResult = await makeGetApiCall("shopifySyncRuns");
      if (!active) {
        return;
      }

      setLoading(false);
      if (callResult.success) {
        const nextRuns = callResult.data || [];
        setRuns(nextRuns);
        setError(null);
        const stillRunning = nextRuns.some(
          (run) => run.status === "queued" || run.status === "processing"
        );
        if (stillRunning) {
          timer = setTimeout(loadRuns, 5000);
        }
      } else if (callResult.errorStatus === 401) {
        logout();
      } else {
        setError("Could not load run history.");
      }
    };

    loadRuns();

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [logout]);

  return (
    <div className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll py-6">
        <div className="px-4 sm:px-6">
          <div className="flex items-start justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              Run all history
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                onClick={() => setOpen({ childComponent: null, open: false })}
              >
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-500">
            Your queued, running, and finished price updates.
          </p>
        </div>
        <div className="relative mt-6 flex-1 px-4 sm:px-6">
          {loading ? (
            <p className="text-sm text-gray-500">Loading runs…</p>
          ) : error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : runs.length === 0 ? (
            <p className="text-sm text-gray-500">No runs yet.</p>
          ) : (
            <div className="space-y-3">
              {runs.map((run) => (
                <div
                  key={run.id}
                  className="rounded-md border border-gray-200 p-4"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-gray-900">
                      Run {run.id}
                    </p>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                        statusStyles[run.status] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {run.status}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-gray-600">
                    {run.processed_products} of {run.total_products} products
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Requested {displayDate(run.created_at)}
                  </p>
                  {run.finished_at ? (
                    <p className="mt-1 text-xs text-gray-500">
                      Finished {displayDate(run.finished_at)}
                    </p>
                  ) : null}
                  {run.error_message ? (
                    <p className="mt-2 text-sm text-red-600">
                      {run.error_message}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
